import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { BigNumber } from "ethers";
import { formatEther, parseEther } from "ethers/lib/utils";
import { useRef, useState } from "react";
import { useBalance, useNetwork, useSigner } from "wagmi";

import analyticsInstance from "../../config/analytics";

import { downgradeToken } from "../../helpers/tokens";

export function TokenDowngrader({ account, tokenBalance, setRefetch }) {
  const [amount, setAmount] = useState(BigNumber.from(0));
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [showDowngraded, setShowDowngraded] = useState(false);
  const [txError, setTxError] = useState();

  const inputRef = useRef(null);
  const { chain } = useNetwork();
  const signer = useSigner();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: useBalanceData,
    isError: useBalanceErrored,
    isFetching: useBalanceLoading,
  } = useBalance({
    addressOrName: account,
    token: tokenBalance.tokenxAddress,
    watch: true,
  });

  const clearState = () => {
    setTxError(null);
    setShowDowngraded(false);
  };

  return (
    <>
      <Button
        size="xs"
        onClick={() => {
          onOpen();
          analyticsInstance.track("TokenDowngraderClicked");
        }}
      >
        🔻 downgrade
      </Button>

      <Modal isOpen={isOpen} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            "Downgrade" {tokenBalance.tokenxSymbol}&nbsp;➡️&nbsp;{tokenBalance.erc20Symbol}
          </ModalHeader>

          <ModalBody>
            <Text fontSize="lg" mb="1em">
              Downgrade your Super Tokens into ERC20 tokens.
            </Text>
            <Link display="inline-block" fontSize="xs" mb="2em" href="/faq#erc20-tokenx" isExternal>
              Check the FAQ to learn more about Super Tokens.
            </Link>

            <Input
              type="number"
              size="lg"
              step={1}
              min={0}
              max={useBalanceData?.formatted}
              onChange={(evt) => setAmount(parseEther(evt.target.value))}
              defaultValue={formatEther(amount)}
              disabled={isDowngrading}
              ref={inputRef}
            />

            <Text my="1em">
              <Button
                size="sm"
                onClick={() => {
                  setAmount(useBalanceData.value);
                  inputRef.current.value = formatEther(useBalanceData.value);
                  analyticsInstance.track("TokenDowngradeAvailableBalanceClicked");
                }}
              >
                Set available balance
              </Button>
              &nbsp;of&nbsp;
              {useBalanceLoading ? (
                <Spinner as="span" ml="1em" />
              ) : useBalanceErrored ? (
                "ERROR"
              ) : (
                useBalanceData.formatted
              )}
              &nbsp;
              {tokenBalance.tokenxSymbol}.
            </Text>

            {showDowngraded && (
              <Alert status="success" variant="subtle">
                <AlertIcon />
                Your tokens have been converted back to ERC20!
              </Alert>
            )}

            {txError && (
              <Alert status="error" variant="subtle">
                <AlertIcon />
                <Box>
                  <AlertTitle>Sorry, something's wrong...</AlertTitle>
                  <AlertDescription>
                    Error downgrading your tokens. This can happen if you are trying to downgrade more than your
                    balance, especially if you are currently streaming the token.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </ModalBody>

          <ModalFooter py="1em">
            <Button
              colorScheme="green"
              size="md"
              disabled={isDowngrading || amount.eq(0) || amount.gt(useBalanceData.value)}
              isLoading={isDowngrading}
              onClick={async () => {
                // clear state
                clearState();

                // set loading
                setIsDowngrading(true);

                // downgrade
                downgradeToken(chain.id, signer.data, tokenBalance.tokenxSymbol, amount)
                  .then((result) => {
                    setShowDowngraded(true);
                    setIsDowngrading(false);
                    setAmount(BigNumber.from(0)); // clear the input
                  })
                  .catch((err) => {
                    setTxError(err);
                    setIsDowngrading(false);

                    analyticsInstance.track("DowngradeError", {
                      tokenx: tokenBalance.tokenxSymbol,
                      amount: amount.toString(),
                      error: err,
                    });

                    console.error(err);
                  });

                analyticsInstance.track("DowngradeClicked");
              }}
            >
              Downgrade 🔻
            </Button>

            <Button
              colorScheme="red"
              size="md"
              onClick={() => {
                if (showDowngraded && setRefetch) setRefetch(showDowngraded);
                clearState();
                onClose();
              }}
              ml="0.5em"
              isDisabled={isDowngrading}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
